import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  CalendarCheck, CalendarStar, Heart, House, SquaresFour, Users, Megaphone, Percent,
  Ticket, ClipboardText, Lightning, BookOpen, ChartBar, Gear, SignOut, List, X, Plugs, Toolbox,
  Browsers, Gift, CaretDown, QrCode, Bell, BellRinging, ArrowsLeftRight, Lock, Phone,
  ShieldCheck,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { BrandLogo } from "../context/BrandingContext";
import usePendingCount from "../hooks/usePendingCount";
import AssistantWidget from "./AssistantWidget";
import api from "../lib/api";
import { toast } from "sonner";
import { useEffect } from "react";

const NAV = [
  { to: "/", label: "Oggi", icon: House, end: true },
  { to: "/clienti", label: "Clienti", icon: Users },
  {
    to: "/prenotazioni", label: "Prenotazioni", icon: CalendarCheck, children: [
      { to: "/moduli", label: "Moduli Web", icon: Browsers },
      { to: "/sala", label: "Panoramica Sale", icon: SquaresFour },
      { to: "/coda", label: "Coda", icon: Users, feature: "coda" },
    ],
  },
  {
    to: "/marketing", label: "Marketing", icon: Megaphone, children: [
      { to: "/coupon", label: "Coupon", icon: Ticket, feature: "coupon" },
      { to: "/eventi", label: "Eventi", icon: CalendarStar },
      { to: "/promozioni", label: "Promozioni", icon: Percent },
      { to: "/fidelity", label: "Fidelity", icon: Heart, feature: "fidelity" },
      { to: "/sondaggi", label: "Sondaggi", icon: ClipboardText, feature: "sondaggi" },
      { to: "/gift-card", label: "Gift Card", icon: Gift, feature: "giftcard" },
    ],
  },
  {
    label: "Dashboard", icon: ChartBar, children: [
      { to: "/statistiche", label: "Statistiche", icon: ChartBar },
    ],
  },
  { to: "/menu", label: "Menu", icon: BookOpen },
  { to: "/haccp", label: "HACCP", icon: ShieldCheck },
  { to: "/automazioni", label: "Automazioni", icon: Lightning, feature: "automazioni" },
  { to: "/centralino", label: "Centralino", icon: Phone, feature: "centralino" },
  { to: "/integrazioni", label: "Integrazioni", icon: Plugs },
  { to: "/impostazioni", label: "Impostazioni", icon: Gear },
  { to: "/strumenti", label: "Strumenti", icon: Toolbox },
];

const MOBILE_MAIN = [NAV[0], NAV[2], NAV[1], NAV[3]]; // Oggi, Prenotazioni, Clienti, Marketing

// Vista Sala (stile Cooperto): solo operatività, niente gestione
const SALA_NAV = [
  { to: "/scanner", label: "Scanner QR", icon: QrCode },
  { to: "/", label: "Oggi", icon: House, end: true },
  { to: "/prenotazioni", label: "Prenotazioni", icon: CalendarCheck },
  { to: "/prenotazioni?stato=da_confermare", label: "Richieste", icon: Bell },
  { to: "/sala", label: "Panoramica Sale", icon: SquaresFour },
  { to: "/clienti", label: "Contatti", icon: Users },
  { to: "/fidelity", label: "Fidelity", icon: Heart, feature: "fidelity" },
  { to: "/gift-card", label: "Gift Card", icon: Gift, feature: "giftcard" },
  { to: "/coda", label: "Coda", icon: Users, feature: "coda" },
  { to: "/haccp", label: "HACCP", icon: ShieldCheck },
];

const SALA_MOBILE_MAIN = [SALA_NAV[0], SALA_NAV[2], SALA_NAV[4], SALA_NAV[5]];

export default function Layout() {
  const { user, logout, plan } = useAuth();
  const navigate = useNavigate();
  const isLocked = (f) => !!f && Array.isArray(plan?.features) && !plan.features.includes(f);
  const goUpgrade = (f) => navigate(`/upgrade?feature=${f}`);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState({});
  const childActive = (item) => !!item.children?.some(
    (c) => location.pathname === c.to || location.pathname.startsWith(c.to + "/"));
  const groupOpen = (item) => openGroups[item.label] ?? childActive(item);
  const toggleGroup = (item) => setOpenGroups((s) => ({ ...s, [item.label]: !groupOpen(item) }));
  const tid = (label) => `nav-${label.toLowerCase().replace(/\s/g, "-")}`;
  const canSwitch = ["admin", "manager"].includes(user?.role);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("dishenda_view") || "admin");
  const salaMode = user?.role === "sala" || (canSwitch && viewMode === "sala");
  const navActive = salaMode ? SALA_NAV : NAV;
  const mobileMain = salaMode ? SALA_MOBILE_MAIN : MOBILE_MAIN;
  const toggleView = () => {
    const next = salaMode ? "admin" : "sala";
    setViewMode(next);
    localStorage.setItem("dishenda_view", next);
    if (next === "sala") navigate("/");
  };
  const [restaurantName, setRestaurantName] = useState("Dishenda");
  const { pendingCount } = usePendingCount();

  // Push PWA: attiva/disattiva le notifiche su questo dispositivo + badge sull'icona home
  const urlBase64ToUint8Array = (base64String) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    return Uint8Array.from([...window.atob(base64)].map((c) => c.charCodeAt(0)));
  };
  const [pushState, setPushState] = useState("unsupported"); // unsupported | off | on | denied

  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return;
    navigator.serviceWorker.ready.then(async (reg) => {
      const sub = await reg.pushManager.getSubscription().catch(() => null);
      setPushState(sub ? "on" : (Notification.permission === "denied" ? "denied" : "off"));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if ("setAppBadge" in navigator) {
      if (pendingCount > 0) navigator.setAppBadge(pendingCount).catch(() => {});
      else navigator.clearAppBadge?.().catch(() => {});
    }
  }, [pendingCount]);

  const togglePush = async () => {
    if (pushState === "on") {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await api.post("/notifications/push/unsubscribe", { endpoint: sub.endpoint, keys: {} }).catch(() => {});
          await sub.unsubscribe();
        }
        setPushState("off");
        toast.success("Notifiche push disattivate su questo dispositivo");
      } catch { toast.error("Errore nella disattivazione delle notifiche"); }
      return;
    }
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setPushState("denied");
        toast.error("Notifiche bloccate dal browser: riattivale dalle impostazioni del telefono");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/notifications/push/public-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      await api.post("/notifications/push/subscribe", sub.toJSON());
      setPushState("on");
      toast.success("Notifiche push attive: ti avvisiamo a ogni nuova prenotazione");
    } catch { toast.error("Impossibile attivare le notifiche push su questo dispositivo"); }
  };

  useEffect(() => {
    api.get("/settings").then((r) => {
      if (r.data?.restaurant_name) setRestaurantName(r.data.restaurant_name);
    }).catch(() => {});
  }, []);

  // Polling avvisi template WhatsApp: toast quando Meta approva/rifiuta un template
  useEffect(() => {
    if (user?.role === "sala" || salaMode) return;
    let stop = false;
    const check = () => {
      api.get("/integrations/whatsapp/templates/notices").then(async (r) => {
        const items = r.data || [];
        if (stop || !items.length) return;
        for (const n of items) {
          if (n.new_status === "APPROVED") toast.success(`Template "${n.name}" approvato da Meta: ora puoi usarlo nelle campagne`);
          else if (n.new_status === "REJECTED") toast.error(`Template "${n.name}" rifiutato da Meta${n.rejected_reason ? ` — ${n.rejected_reason}` : ""}`);
          else toast.info(`Template "${n.name}" — nuovo stato: ${n.new_status}`);
        }
        await api.post("/integrations/whatsapp/templates/notices/read", { ids: items.map((n) => n.id) }).catch(() => {});
      }).catch(() => {});
    };
    check();
    const iv = setInterval(check, 60000);
    const onVis = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { stop = true; clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, [user?.role]);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-zinc-100 restaurant-area" data-testid="app-layout">
      {["pagamento_fallito", "tolleranza"].includes(user?.account_status) && (
        <div className="bg-amber-400 text-zinc-950 px-4 py-2.5 text-xs sm:text-sm font-semibold flex items-center justify-center gap-2"
          data-testid="banner-tolleranza">
          Pagamento non riuscito: il tuo account è in tolleranza
          {user?.grace_ends_at ? ` fino al ${new Date(user.grace_ends_at).toLocaleDateString("it-IT")}` : ""}.
          Regolarizza il pagamento per evitare la sospensione.
        </div>
      )}
      {/* Sidebar desktop */}
      <aside className="hidden sm:flex fixed inset-y-0 left-0 w-56 flex-col border-r border-white/10 z-30" style={{ backgroundColor: "#0B1526" }}>
        <div className="px-5 py-7 border-b border-white/10 flex flex-col items-center gap-2">
          <BrandLogo slot="main" fallbackClass="font-display font-extrabold text-3xl tracking-tight text-white" className="h-16 w-auto max-w-full object-contain" />
          <div className="text-xs text-zinc-400 truncate max-w-full" data-testid="restaurant-name">{restaurantName}</div>
          {plan && (
            <button onClick={() => navigate("/impostazioni?tab=piano")} data-testid="sidebar-plan-badge"
              className="mt-1.5 self-start text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border border-cyan-800/60 text-cyan-300/90 hover:text-cyan-200 hover:border-cyan-500 transition-colors">
              {plan.comp ? "Omaggio" : plan.plan_name ? `Piano ${plan.plan_name}` : "Prova gratuita"}
            </button>
          )}
        </div>
        {salaMode && (
          <div className="px-3 pt-3">
            <span className="block text-center text-[10px] uppercase tracking-widest font-bold text-cyan-300 bg-cyan-400/10 border border-cyan-400/30 rounded-md py-1"
              data-testid="sala-mode-badge">Vista Sala</span>
          </div>
        )}
        {canSwitch && (
          <div className="px-3 pt-3">
            <button onClick={toggleView} data-testid="view-switch"
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-white/15 text-zinc-300 text-xs font-bold uppercase tracking-wider hover:bg-white/5 hover:text-white transition-colors">
              <ArrowsLeftRight size={14} weight="bold" /> {salaMode ? "Passa ad Admin" : "Vista Sala"}
            </button>
          </div>
        )}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {navActive.map((item) => {
            const { to, label, icon: Icon, end, children } = item;
            const badge = label === "Prenotazioni" && pendingCount > 0 && (
              <span data-testid="nav-badge-prenotazioni"
                className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold flex items-center justify-center">
                {pendingCount > 99 ? "99+" : pendingCount}
              </span>
            );
            if (!children) {
              if (isLocked(item.feature)) {
                return (
                  <button key={to} onClick={() => goUpgrade(item.feature)} data-testid={`nav-locked-${tid(label)}`}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-r-md text-sm font-medium text-zinc-600 hover:bg-white/5 hover:text-zinc-400 transition-colors duration-150">
                    <Icon size={18} weight="bold" /> {label}
                    <Lock size={13} weight="bold" className="ml-auto" />
                  </button>
                );
              }
              return (
                <NavLink key={to} to={to} end={end} data-testid={tid(label)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-r-md text-sm font-medium transition-colors duration-150 ${
                      isActive ? "bg-white/5 text-cyan-300 shadow-[inset_3px_0_0_#018BFA]" : "text-zinc-400 hover:bg-white/5 hover:text-white"}`}>
                  <Icon size={18} weight="bold" /> {label}
                  {badge}
                </NavLink>
              );
            }
            const open = groupOpen(item);
            return (
              <div key={label}>
                <div className={`flex items-center rounded-r-md transition-colors duration-150 ${
                  childActive(item) ? "bg-white/5" : ""}`}>
                  {to ? (
                    <NavLink to={to} data-testid={tid(label)}
                      className={({ isActive }) =>
                        `flex flex-1 items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
                          isActive ? "text-cyan-300" : childActive(item) ? "text-white" : "text-zinc-400 hover:text-white"}`}>
                      <Icon size={18} weight="bold" /> {label}
                      {badge}
                    </NavLink>
                  ) : (
                    <button onClick={() => toggleGroup(item)} data-testid={tid(label)}
                      className={`flex flex-1 items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
                        childActive(item) ? "text-white" : "text-zinc-400 hover:text-white"}`}>
                      <Icon size={18} weight="bold" /> {label}
                    </button>
                  )}
                  <button onClick={() => toggleGroup(item)} data-testid={`nav-toggle-${label.toLowerCase().replace(/\s/g, "-")}`}
                    aria-label={`Espandi ${label}`}
                    className="p-2 mr-1 text-zinc-500 hover:text-white transition-colors shrink-0">
                    <CaretDown size={14} weight="bold" className={`transition-transform duration-150 ${open ? "" : "-rotate-90"}`} />
                  </button>
                </div>
                {open && (
                  <div className="ml-5 pl-3 mt-0.5 border-l border-white/10 space-y-0.5">
                    {children.map((c) => (
                      isLocked(c.feature) ? (
                        <button key={c.to} onClick={() => goUpgrade(c.feature)} data-testid={`nav-locked-${tid(c.label)}`}
                          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-r-md text-[13px] font-medium text-zinc-600 hover:bg-white/5 hover:text-zinc-400 transition-colors duration-150">
                          <c.icon size={16} weight="bold" /> {c.label}
                          <Lock size={12} weight="bold" className="ml-auto" />
                        </button>
                      ) : (
                      <NavLink key={c.to} to={c.to} data-testid={tid(c.label)}
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 px-3 py-2 rounded-r-md text-[13px] font-medium transition-colors duration-150 ${
                            isActive ? "bg-white/5 text-cyan-300" : "text-zinc-500 hover:bg-white/5 hover:text-white"}`}>
                        <c.icon size={16} weight="bold" /> {c.label}
                      </NavLink>
                      )
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        <div className="border-t border-white/10 p-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="w-9 h-9 rounded-full bg-cyan-400/15 text-cyan-300 flex items-center justify-center text-sm font-black shrink-0" data-testid="user-avatar">
              {(user?.name || "?")[0].toUpperCase()}
            </span>
            <div className="min-w-0">
              <div className="text-sm font-medium text-white truncate">{user?.name}</div>
              <div className="text-xs text-zinc-400 capitalize">{user?.role}</div>
            </div>
          </div>
          {pushState !== "unsupported" && (
            <button onClick={togglePush} data-testid="push-toggle-desktop"
              title={pushState === "on" ? "Notifiche push attive — tocca per disattivare" : "Attiva le notifiche push per le nuove prenotazioni"}
              className={`p-2 rounded-md transition-colors shrink-0 ${pushState === "on" ? "text-emerald-400 hover:bg-white/10" : "text-zinc-400 hover:bg-white/10 hover:text-white"}`}>
              <BellRinging size={18} weight={pushState === "on" ? "fill" : "regular"} />
            </button>
          )}
          <button onClick={doLogout} data-testid="logout-btn" title="Esci"
            className="p-2 rounded-md text-zinc-400 hover:bg-white/10 hover:text-white transition-colors shrink-0">
            <SignOut size={18} />
          </button>
        </div>
      </aside>

      {/* Header mobile */}
      <header className="sm:hidden sticky top-0 z-30 border-b border-white/10 px-4 h-16 flex items-center justify-between" style={{ backgroundColor: "#0B1526" }}>
        <div>
          <BrandLogo slot="compact" fallbackClass="font-display font-extrabold text-2xl tracking-tight text-white" className="h-10 w-auto object-contain inline-block" />
          <span className="ml-2 text-xs text-zinc-400">{restaurantName}</span>
        </div>
        <div className="flex items-center gap-1">
          {canSwitch && (
            <button onClick={toggleView} data-testid="view-switch-mobile"
              className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-cyan-300 text-[11px] font-bold uppercase tracking-wider hover:bg-white/5 transition-colors">
              <ArrowsLeftRight size={16} weight="bold" /> {salaMode ? "Admin" : "Sala"}
            </button>
          )}
          {pushState !== "unsupported" && (
            <button onClick={togglePush} data-testid="push-toggle-mobile"
              title={pushState === "on" ? "Notifiche push attive — tocca per disattivare" : "Attiva le notifiche push per le nuove prenotazioni"}
              className={`p-2 transition-colors ${pushState === "on" ? "text-emerald-400" : "text-zinc-400"}`}>
              <BellRinging size={20} weight={pushState === "on" ? "fill" : "regular"} />
            </button>
          )}
          <button onClick={doLogout} data-testid="logout-btn-mobile" className="p-2 text-zinc-400">
            <SignOut size={20} />
          </button>
        </div>
      </header>
      <AssistantWidget />

      <main className="sm:ml-56 pb-24 sm:pb-8 px-3 sm:px-6 py-4 sm:py-6 max-w-6xl">
        <Outlet />
      </main>

      {/* Bottom nav mobile */}
      <nav className="sm:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-zinc-200 grid grid-cols-5 h-16" data-testid="mobile-nav">
        {mobileMain.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} data-testid={`mnav-${label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors ${
                isActive ? "text-zinc-900" : "text-zinc-400"}`}>
            <span className="relative">
              <Icon size={22} weight="bold" />
              {label === "Prenotazioni" && pendingCount > 0 && (
                <span data-testid="mnav-badge-prenotazioni"
                  className="absolute -top-1.5 -right-2.5 min-w-4 h-4 px-1 rounded-full bg-emerald-500 text-white text-[9px] font-bold flex items-center justify-center">
                  {pendingCount > 99 ? "99+" : pendingCount}
                </span>
              )}
            </span> {label}
          </NavLink>
        ))}
        <button onClick={() => setMenuOpen(true)} data-testid="mnav-altro"
          className="flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium text-zinc-400">
          <List size={22} weight="bold" /> Altro
        </button>
      </nav>

      {/* Menu "Altro" mobile */}
      {menuOpen && (
        <div className="sm:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setMenuOpen(false)}>
          <div className="absolute bottom-0 inset-x-0 bg-white rounded-t-xl p-4 pb-8 max-h-[70vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} data-testid="mobile-more-menu">
            <div className="flex items-center justify-between mb-3">
              <span className="font-display font-bold text-zinc-900">Menu</span>
              <button onClick={() => setMenuOpen(false)} className="p-2 text-zinc-500"><X size={20} /></button>
            </div>
            <div className="space-y-4">
              {navActive.filter((n) => !mobileMain.includes(n) || n.children).map((item) => {
                const { to, label, icon: Icon, children } = item;
                if (!children) {
                  if (isLocked(item.feature)) {
                    return (
                      <button key={to} onClick={() => { setMenuOpen(false); goUpgrade(item.feature); }}
                        data-testid={`mnav-locked-${label.toLowerCase().replace(/\s/g, "-")}`}
                        className="w-full flex items-center gap-2 px-3 py-3 rounded-md border text-sm font-medium text-zinc-400 border-zinc-200">
                        <Icon size={18} weight="bold" /> {label}
                        <Lock size={13} weight="bold" className="ml-auto" />
                      </button>
                    );
                  }
                  return (
                    <NavLink key={to} to={to} onClick={() => setMenuOpen(false)} data-testid={`mnav-${label.toLowerCase().replace(/\s/g, "-")}`}
                      className={({ isActive }) =>
                        `flex items-center gap-2 px-3 py-3 rounded-md border text-sm font-medium ${
                          isActive ? "bg-zinc-900 text-white border-zinc-900" : "text-zinc-700 border-zinc-200"}`}>
                      <Icon size={18} weight="bold" /> {label}
                    </NavLink>
                  );
                }
                return (
                  <div key={label}>
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-zinc-400 mb-1.5">
                      <Icon size={14} weight="bold" /> {label}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {children.map((c) => (
                        isLocked(c.feature) ? (
                          <button key={c.to} onClick={() => { setMenuOpen(false); goUpgrade(c.feature); }}
                            data-testid={`mnav-locked-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                            className="w-full flex items-center gap-2 px-3 py-3 rounded-md border text-sm font-medium text-zinc-400 border-zinc-200">
                            <c.icon size={18} weight="bold" /> {c.label}
                            <Lock size={13} weight="bold" className="ml-auto" />
                          </button>
                        ) : (
                        <NavLink key={c.to} to={c.to} onClick={() => setMenuOpen(false)} data-testid={`mnav-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                          className={({ isActive }) =>
                            `flex items-center gap-2 px-3 py-3 rounded-md border text-sm font-medium ${
                              isActive ? "bg-zinc-900 text-white border-zinc-900" : "text-zinc-700 border-zinc-200"}`}>
                          <c.icon size={18} weight="bold" /> {c.label}
                        </NavLink>
                        )
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
